import { Club, CreateClubRequest, CreateCameraRequest, UpdateCameraRequest, CreateSponsorRequest, Camera, CameraScheduleDay, CameraScheduleResponse  } from '../types';
import { msalInstance } from '../index';
import { apiRequest } from '../auth/msalConfig';

const API_BASE = process.env.REACT_APP_API_BASE ?? 'https://api.19thview.com.au';

async function getToken(): Promise<string> {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return '';
    try {
        const result = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0]
        });
        return result.accessToken;
    } catch (err) {
        console.error('Token error:', err);
        await msalInstance.acquireTokenRedirect(apiRequest);
        return '';
    }
}

async function fetchJson<T>(path: string, options?: RequestInit): Promise<T> {
    const token = await getToken();
    const resp = await fetch(`${API_BASE}${path}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...options?.headers,
        },
        ...options,
    });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`API error ${resp.status}: ${text}`);
    }
    if (resp.status === 204) return undefined as T;
    const text = await resp.text();
    if (!text) return undefined as T;
    return JSON.parse(text);
}

export const clubsApi = {
    getAll: () =>
        fetchJson<Club[]>('/api/admin/clubs'),

    getById: (id: string) =>
        fetchJson<Club>(`/api/admin/clubs/${id}`),

    create: (data: CreateClubRequest) =>
        fetchJson<Club>('/api/admin/clubs', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    update: (id: string, data: Partial<CreateClubRequest> & { isActive: boolean }) =>
        fetchJson<void>(`/api/admin/clubs/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    enable: (id: string) =>
        fetchJson<void>(`/api/admin/clubs/${id}/enable`, { method: 'POST' }),

    disable: (id: string) =>
        fetchJson<void>(`/api/admin/clubs/${id}/disable`, { method: 'POST' }),

    getCameraStatus: (clubId: string) =>
        fetchJson<{ id: string; isLive: boolean }[]>(
            `/api/admin/clubs/${clubId}/cameras/status`
        ),

    uploadImage: async (clubId: string, type: 'logo' | 'hero', file: File) => {
        const token = await getToken();
        const formData = new FormData();
        formData.append('file', file);
        const resp = await fetch(`${API_BASE}/api/admin/clubs/${clubId}/${type}`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData,
        });
        if (!resp.ok) {
            const text = await resp.text();
            throw new Error(`API error ${resp.status}: ${text}`);
        }
        return resp.json();
    },
};

export const camerasApi = {
    add: (clubId: string, data: CreateCameraRequest) =>
        fetchJson<Camera>(`/api/admin/clubs/${clubId}/cameras`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),
    update: (clubId: string, cameraId: string, data: UpdateCameraRequest) =>
        fetchJson<Camera>(`/api/admin/clubs/${clubId}/cameras/${cameraId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),
    delete: (clubId: string, cameraId: string) =>
        fetchJson<void>(`/api/admin/clubs/${clubId}/cameras/${cameraId}`, {
            method: 'DELETE',
        }),
    getSchedule: (clubId: string, cameraId: string) =>
        fetchJson<CameraScheduleResponse>(
            `/api/admin/clubs/${clubId}/cameras/${cameraId}/schedule`),

    updateSchedule: (clubId: string, cameraId: string, days: CameraScheduleDay[]) =>
        fetchJson<void>(
            `/api/admin/clubs/${clubId}/cameras/${cameraId}/schedule`, {
                method: 'PUT',
                body: JSON.stringify({ days }),
            }),
    setFeatured: (clubId: string, cameraId: string, endpoint: string) =>
        fetchJson<void>(`/api/admin/clubs/${clubId}/cameras/${cameraId}/${endpoint}`, {
            method: 'POST',
        }),
};

export const sponsorsApi = {
    add: (clubId: string, data: CreateSponsorRequest) =>
        fetchJson<void>(`/api/admin/clubs/${clubId}/sponsors`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    update: (clubId: string, sponsorId: string, data: CreateSponsorRequest) =>
        fetchJson<void>(`/api/admin/clubs/${clubId}/sponsors/${sponsorId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    delete: (clubId: string, sponsorId: string) =>
        fetchJson<void>(`/api/admin/clubs/${clubId}/sponsors/${sponsorId}`, {
            method: 'DELETE',
        }),
};

export const templatesApi = {
    getForClub: (clubId: string) =>
        fetchJson(`/api/admin/clubs/${clubId}/templates`),

    create: (clubId: string, data: any) =>
        fetchJson(`/api/admin/clubs/${clubId}/templates`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    update: (clubId: string, id: string, data: any) =>
        fetchJson(`/api/admin/clubs/${clubId}/templates/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    delete: (clubId: string, id: string) =>
        fetchJson(`/api/admin/clubs/${clubId}/templates/${id}`, {
            method: 'DELETE',
        }),
};

export const competitionsApi = {
    getForClub: (clubId: string) =>
        fetchJson(`/api/admin/clubs/${clubId}/competitions`),

    applyTemplate: (clubId: string, data: any) =>
        fetchJson(`/api/admin/clubs/${clubId}/competitions/apply-template`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    update: (clubId: string, id: string, data: any) =>
        fetchJson(`/api/admin/clubs/${clubId}/competitions/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    delete: (clubId: string, id: string) =>
        fetchJson(`/api/admin/clubs/${clubId}/competitions/${id}`, {
            method: 'DELETE',
        }),

    setCameraSponsor: (clubId: string, competitionId: string, cameraId: string, sponsorId: string | null) =>
        fetchJson(`/api/admin/clubs/${clubId}/competitions/${competitionId}/cameras/${cameraId}/sponsor`, {
            method: 'PUT',
            body: JSON.stringify({ sponsorId }),
        }),
};

// ── Content / Adverts API ─────────────────────────────────────────────────────

async function uploadWithProgress(
    path: string,
    formData: FormData,
    onProgress: (pct: number) => void
): Promise<any> {
    const token = await getToken();
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE}${path}`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable)
                onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try { resolve(JSON.parse(xhr.responseText)); }
                catch { resolve({}); }
            } else {
                reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
            }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(formData);
    });
}

export const contentApi = {
    list: (clubId: string | null, type?: string) => {
        const params = new URLSearchParams();
        if (clubId) params.set('clubId', clubId);
        if (type)   params.set('type', type);
        return fetchJson<any[]>(`/api/content?${params}`);
    },

    getStatus: (id: string) =>
        fetchJson<{ id: string; transcodeStatus: string; durationSeconds: number }>
            (`/api/content/${id}/status`),

    upload: (formData: FormData, onProgress: (pct: number) => void) =>
        uploadWithProgress('/api/content/upload', formData, onProgress),

    update: (id: string, data: {
        title: string;
        weightSeconds: number;
        durationSeconds: number;
        activeFrom?: string;
        activeTo?: string;
        isActive: boolean;
    }) => fetchJson<void>(`/api/content/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    delete: (id: string) =>
        fetchJson<void>(`/api/content/${id}`, { method: 'DELETE' }),
};